import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Modal,
  ModalBody,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { userHasAnyRole } from "../../../services/authService";
import {
  createCatalogCategory,
  deleteCatalogCategory,
  getCatalogCategories,
  updateCatalogCategory,
} from "../../../services/catalogCategoryService";
import { CatalogCategory } from "../../../types/catalogCategory";
import { PaginationMeta } from "../../../types/api";
import CategoryFormModal, { CategoryFormValues } from "./CategoryFormModal";

const PER_PAGE = 15;

const CatalogCategoriesPage = () => {
  const { t } = useTranslation();
  const isOwner = userHasAnyRole(["owner"]);

  document.title = t("settings.catalog_categories.pageTitle");

  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CatalogCategory | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CatalogCategory | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCatalogCategories({
        page,
        per_page: PER_PAGE,
      });
      setCategories(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("settings.catalog_categories.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [page, t]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const openCreate = () => {
    setEditingCategory(null);
    setModalOpen(true);
  };

  const openEdit = (category: CatalogCategory) => {
    setEditingCategory(category);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingCategory(null);
  };

  const handleSubmit = async (values: CategoryFormValues) => {
    setSubmitting(true);
    const payload = {
      name: values.name.trim(),
      sort_order: values.sort_order,
      is_active: values.is_active,
    };

    try {
      if (editingCategory) {
        const response = await updateCatalogCategory(editingCategory.id, payload);
        toast.success(response.message);
      } else {
        const response = await createCatalogCategory(payload);
        toast.success(response.message);
      }

      closeModal();

      if (!editingCategory && page !== 1) {
        setPage(1);
      } else {
        await fetchCategories();
      }
    } catch (error: any) {
      toast.error(error?.data || t("settings.catalog_categories.toast.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      const response = await deleteCatalogCategory(deleteTarget.id);
      toast.success(response.message);
      setDeleteModal(false);
      setDeleteTarget(null);

      const isLastItemOnPage = categories.length === 1 && page > 1;
      if (isLastItemOnPage) {
        setPage((current) => current - 1);
      } else {
        await fetchCategories();
      }
    } catch (error: any) {
      toast.error(error?.data || t("settings.catalog_categories.toast.deleteError"));
    }
  };

  const totalPages = meta?.last_page ?? 1;

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("settings.catalog_categories.breadcrumb.title")}
            pageTitle={t("settings.catalog_categories.breadcrumb.pageTitle")}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                  <h4 className="card-title mb-0 flex-grow-1">
                    {t("settings.catalog_categories.card.title")}
                  </h4>
                  {isOwner ? (
                    <Button color="primary" onClick={openCreate}>
                      <i className="ri-add-line align-bottom me-1" />
                      {t("settings.catalog_categories.actions.new")}
                    </Button>
                  ) : null}
                </CardHeader>

                <CardBody>
                  {loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : categories.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <p className="mb-3">{t("settings.catalog_categories.empty.noData")}</p>
                      {isOwner ? (
                        <Button color="soft-primary" onClick={openCreate}>
                          {t("settings.catalog_categories.actions.createFirst")}
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table hover className="align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("settings.catalog_categories.table.name")}</th>
                              <th style={{ width: 120 }}>
                                {t("settings.catalog_categories.table.sortOrder")}
                              </th>
                              <th style={{ width: 120 }}>
                                {t("settings.catalog_categories.table.status")}
                              </th>
                              {isOwner ? (
                                <th className="text-end" style={{ width: 120 }}>
                                  {t("settings.catalog_categories.table.actions")}
                                </th>
                              ) : null}
                            </tr>
                          </thead>
                          <tbody>
                            {categories.map((category) => (
                              <tr key={category.id}>
                                <td className="fw-medium">{category.name}</td>
                                <td>{category.sort_order}</td>
                                <td>
                                  <Badge
                                    color={category.is_active ? "success" : "secondary"}
                                    className="text-uppercase"
                                  >
                                    {category.is_active
                                      ? t("settings.catalog_categories.status.active")
                                      : t("settings.catalog_categories.status.inactive")}
                                  </Badge>
                                </td>
                                {isOwner ? (
                                  <td className="text-end">
                                    <Button
                                      color="soft-primary"
                                      size="sm"
                                      className="me-1"
                                      title={t("settings.catalog_categories.actions.edit")}
                                      onClick={() => openEdit(category)}
                                    >
                                      <i className="ri-pencil-line" />
                                    </Button>
                                    <Button
                                      color="soft-danger"
                                      size="sm"
                                      title={t("settings.catalog_categories.actions.delete")}
                                      onClick={() => {
                                        setDeleteTarget(category);
                                        setDeleteModal(true);
                                      }}
                                    >
                                      <i className="ri-delete-bin-line" />
                                    </Button>
                                  </td>
                                ) : null}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {meta && totalPages > 1 ? (
                        <Row className="align-items-center mt-3 g-3">
                          <Col sm={6}>
                            <p className="text-muted mb-0">
                              {t("settings.catalog_categories.pagination.showing", {
                                from: meta.from ?? 0,
                                to: meta.to ?? 0,
                                total: meta.total,
                              })}
                            </p>
                          </Col>
                          <Col sm={6}>
                            <Pagination className="pagination-separated justify-content-sm-end mb-0">
                              <PaginationItem disabled={page <= 1}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page > 1) {
                                      setPage(page - 1);
                                    }
                                  }}
                                >
                                  {t("settings.catalog_categories.pagination.previous")}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem active>
                                <PaginationLink href="#">
                                  {page} / {totalPages}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem disabled={page >= totalPages}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page < totalPages) {
                                      setPage(page + 1);
                                    }
                                  }}
                                >
                                  {t("settings.catalog_categories.pagination.next")}
                                </PaginationLink>
                              </PaginationItem>
                            </Pagination>
                          </Col>
                        </Row>
                      ) : null}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <CategoryFormModal
        isOpen={modalOpen}
        editingCategory={editingCategory}
        submitting={submitting}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-delete-bin-line display-5 text-danger" />
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t("settings.catalog_categories.delete.title")}</h4>
              <p className="text-muted mx-4 mb-0">
                {deleteTarget
                  ? t("settings.catalog_categories.delete.description", {
                      name: deleteTarget.name,
                    })
                  : t("settings.catalog_categories.delete.fallback")}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button color="light" onClick={() => setDeleteModal(false)}>
              {t("settings.catalog_categories.actions.cancel")}
            </Button>
            <Button color="danger" onClick={confirmDelete}>
              {t("settings.catalog_categories.actions.confirmDelete")}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default CatalogCategoriesPage;
